import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import * as ramda from 'ramda';
import {formValueSelector, change} from 'redux-form';

import {colors} from 'shells/colors/colors';
import {ExpandingFolder} from 'shells/expanding-folder/expanding-folder';
// @ts-expect-error - imported from .js/.jsx file
import {EmptyFolderIcon} from 'shells/icon/svg/empty-folder-icon';
// @ts-expect-error - imported from .js/.jsx file
import {FolderIcon} from 'shells/icon/svg/folder-icon';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import type {NutshellState} from '../../../../store';

import {selectSequenceId} from '../../email-composer-actions';
import type {Template} from '../../templates/template-groups/types';

import {getEmailTemplatesFromMaterializedEmailSequenceTemplateMessages} from '../flatten-templates-in-sequence';
import {appendReplyQuotedSuffixToSequenceState} from '../helpers';
import {FORM_NAME} from '../types';
import type {EmailComposerFormSequenceState} from '../types';

import {EmptyState} from './empty-state';
import {wasPreviouslyMaterialized} from './helpers';
import {TemplateRow} from './template-row';

import styles from './groups-list.module.css';

const valueSelector = formValueSelector(FORM_NAME);
// @ts-expect-error
const getFirstTemplate = ramda.compose(ramda.find(ramda.propEq('position', 0)), ramda.values);

type Props = {
    isLoading: boolean;
    selectedSequenceId: string;
    selectedTemplateId: string;
    setMaterializeTemplateId: (id: string) => void;
    materializedSequenceTemplate: any;
    onTemplateSelect: (selectedSequenceId: string, selectedTemplateId: string) => void;
    isMaterializing: boolean;
    onCreateFromScratch: () => void;
    closeModal: () => void;
    searchValue: string;
    setSearchValue: (searchValue: string) => void;
    replyQuotedSuffixHtml?: string | null;
};

export function GroupsList(props: Props) {
    const {
        setMaterializeTemplateId,
        materializedSequenceTemplate,
        selectedSequenceId,
        onCreateFromScratch,
    } = props;

    const dispatch = useDispatch();
    const {user} = useGetSessionUser();
    const sequences = useSelector((state: NutshellState) => valueSelector(state, 'sequences'));
    const templateGroups = useSelector((state: NutshellState) =>
        valueSelector(state, 'templateGroups')
    );
    const uncategorizedTemplates = useSelector((state: NutshellState) =>
        valueSelector(state, 'uncategorizedTemplates')
    );
    const recentlySentTemplates = useSelector((state: NutshellState) =>
        valueSelector(state, 'recentlySentTemplates')
    );
    const onSetSequences = (newSequences: any) => {
        dispatch(change(FORM_NAME, 'sequences', newSequences));
    };
    const onSelectSequence = (id: string) => {
        dispatch(selectSequenceId(id));
    };
    const setIsRecentSelected = (isRecent: boolean) => {
        dispatch(change(FORM_NAME, 'isRecentSelected', isRecent));
    };
    const isRecentSelected = useSelector((state: NutshellState) =>
        valueSelector(state, 'isRecentSelected')
    );

    const materializedSequenceTemplateId =
        materializedSequenceTemplate && materializedSequenceTemplate.emailSequenceTemplate.id;

    // If we have a materialized sequence template, we need to update the form state to include
    // the new data, e.g. subject, body, delay, etc
    React.useEffect(() => {
        if (materializedSequenceTemplateId) {
            // If the template was already materialized, we need to prevent writing over any
            // local updates made, so we don't overwrite the sequence in the form state.
            if (wasPreviouslyMaterialized(sequences[props.selectedSequenceId])) {
                onSelectSequence(materializedSequenceTemplateId);

                return;
            }

            // Get the new info into the right format for the form state
            const materializedTemplates =
                getEmailTemplatesFromMaterializedEmailSequenceTemplateMessages(
                    materializedSequenceTemplate.materializedEmailSequenceTemplateMessages
                );

            let updatedSequence: EmailComposerFormSequenceState = {
                ...sequences[props.selectedSequenceId],
                emailTemplates: materializedTemplates,
                isMaterialized: true,
            };

            if (props.replyQuotedSuffixHtml) {
                updatedSequence = appendReplyQuotedSuffixToSequenceState(
                    updatedSequence,
                    props.replyQuotedSuffixHtml
                );
            }

            const updatedSequences = {
                ...sequences,
                [props.selectedSequenceId]: updatedSequence,
            };
            onSetSequences(updatedSequences);
            onSelectSequence(materializedSequenceTemplateId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materializedSequenceTemplateId]);

    const onSequenceSelect = (sequence: EmailComposerFormSequenceState, isRecent?: boolean) => {
        // When a sequence is selected we need to _actually_ select
        // its first template, not the row itself.
        // @ts-expect-error
        const firstTemplate = getFirstTemplate(sequence.emailTemplates);

        // // We'll presumably always it, but flow wants us to be sure.
        if (firstTemplate) {
            // @ts-expect-error
            props.onTemplateSelect(sequence.id, firstTemplate.id);
        }

        setIsRecentSelected(Boolean(isRecent));
        setMaterializeTemplateId(sequence.id);
    };

    const groupsArray = templateGroups
        // @ts-expect-error
        .filter((group) => group.emailSequenceTemplates.length > 0)
        // @ts-expect-error
        .map((group) => ({
            ...group,
            emailSequenceTemplates: group.emailSequenceTemplates.map(
                // @ts-expect-error
                (sequence) => sequences[sequence.id]
            ),
        }));

    // @ts-expect-error
    const uncategorizedArray = uncategorizedTemplates.map((template) => sequences[template.id]);
    const isSearching = props.searchValue.length > 0;
    const recentsArray = recentlySentTemplates
        // @ts-expect-error
        .map((template) => sequences[template.id])
        // @ts-expect-error
        .filter((template) => (isSearching ? template.id === selectedSequenceId : true));
    const folderContainsSelectedTemplate = (templateArray: Template[]) => {
        return Boolean(templateArray.find((template) => template.id === props.selectedSequenceId));
    };
    const shouldShowRecent =
        recentsArray.length && (!isSearching || folderContainsSelectedTemplate(recentsArray));
    const shouldShowUncategorized = uncategorizedArray.length > 0;

    const templatesInGroups = React.useMemo(() => [], []);
    // @ts-expect-error
    groupsArray.forEach((group) => templatesInGroups.push(...group.emailSequenceTemplates));

    // If the selected sequence is no longer in the search results, select create from scratch
    React.useEffect(() => {
        if (isSearching && selectedSequenceId) {
            if (
                // @ts-expect-error
                !templatesInGroups.find((template) => template.id === selectedSequenceId) &&
                // @ts-expect-error
                !uncategorizedArray.find((template) => template.id === selectedSequenceId)
            ) {
                onCreateFromScratch();
            }
        }
    }, [
        isSearching,
        onCreateFromScratch,
        selectedSequenceId,
        templatesInGroups,
        uncategorizedArray,
    ]);

    const noTemplatesFound = !groupsArray.length && !uncategorizedArray.length;

    return (
        <>
            {!props.isLoading && noTemplatesFound ? (
                <EmptyState
                    isSearching={isSearching}
                    canManageEmailTemplates={Boolean(
                        user && user.permissions.canManageEmailTemplates
                    )}
                    onCloseModal={props.closeModal}
                    setSearchValue={props.setSearchValue}
                />
            ) : undefined}
            {props.isLoading && noTemplatesFound ? (
                <div className={styles.loading}>
                    <LoadingIcon fill={colors.orangeLt} />
                </div>
            ) : undefined}
            {shouldShowRecent ? (
                <ExpandingFolder title='Recent' isExpanded={true}>
                    {/* @ts-expect-error */}
                    {recentsArray.map((sequence) => (
                        <TemplateRow
                            key={sequence.id}
                            sequence={sequence}
                            onSequenceSelect={(selection: EmailComposerFormSequenceState) => {
                                onSequenceSelect(selection, true);
                            }}
                            onTemplateSelect={props.onTemplateSelect}
                            selectedTemplateId={props.selectedTemplateId}
                            isLoading={props.isMaterializing}
                            isSelected={selectedSequenceId === sequence.id && isRecentSelected}
                        />
                    ))}
                </ExpandingFolder>
            ) : undefined}
            {/* @ts-expect-error */}
            {groupsArray.map((group) => (
                <ExpandingFolder
                    key={group.id}
                    title={group.name}
                    icon={FolderIcon}
                    isExpanded={
                        isSearching || folderContainsSelectedTemplate(group.emailSequenceTemplates)
                    }
                >
                    {/* @ts-expect-error */}
                    {group.emailSequenceTemplates.map((sequence) => (
                        <TemplateRow
                            key={sequence.id}
                            sequence={sequence}
                            onSequenceSelect={(selection: EmailComposerFormSequenceState) => {
                                onSequenceSelect(selection);
                            }}
                            onTemplateSelect={props.onTemplateSelect}
                            selectedTemplateId={props.selectedTemplateId}
                            isLoading={props.isMaterializing}
                            isSelected={selectedSequenceId === sequence.id && !isRecentSelected}
                        />
                    ))}
                </ExpandingFolder>
            ))}
            {shouldShowUncategorized ? (
                <ExpandingFolder title='Uncategorized' icon={EmptyFolderIcon} isExpanded={true}>
                    {/* @ts-expect-error */}
                    {uncategorizedArray.map((sequence) => (
                        <TemplateRow
                            key={sequence.id}
                            sequence={sequence}
                            onSequenceSelect={(selection: EmailComposerFormSequenceState) => {
                                onSequenceSelect(selection);
                            }}
                            onTemplateSelect={props.onTemplateSelect}
                            selectedTemplateId={props.selectedTemplateId}
                            isLoading={props.isMaterializing}
                            isSelected={selectedSequenceId === sequence.id && !isRecentSelected}
                        />
                    ))}
                </ExpandingFolder>
            ) : undefined}
        </>
    );
}
